import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import Report from 'components/Report';
import GroupedLoadingContext from 'contexts/GroupedLoadingContext';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';

import useReport from '../ReportShow/useReport';
import PRINT_LOADING_SELECTOR from './printLoadingSelector';

// The report's own container is a viewport-height scroll container
// (height: calc(100vh - …)), so we DON'T flatten heights (that collapses the
// layout). Instead the renderer grows the viewport to the content height so it
// all fits without scrolling. Here we just strip the body margin and ensure a
// solid background for the capture.
const PrintGlobalStyle = createGlobalStyle`
  body { margin: 0; background: var(--color-bg-primary); }
  /* The scheduled-report PDF is the report content only. Hide the report title
     and the filter/controls bar — both carry the EXCLUDE_HEIGHT (exclude-height-*)
     class — so the first thing in the PDF is the first gadget. The gadget grid
     itself has no such class, so it is unaffected. */
  #print-report-root [class*="exclude-height-"] { display: none !important; }
`;

// Fixed render width for a consistent layout regardless of the runner's viewport.
const PrintRoot = styled.div`
  width: 1280px;
  background: var(--color-bg-primary);
`;

// Poll interval for the "fully rendered" check.
const POLL_MS = 700;
// Absolute safety net so we always signal rather than hanging to the renderer's
// wait timeout.
const MAX_WAIT_MS = 90000;

const PrintReportShow = () => {
  // This screen isn't mounted under a :reportId <Route>, so derive the id from the
  // path rather than useParams (which would be empty here).
  const { pathname } = useLocation();
  const reportId = pathname.split('/print/reports/')[1]?.split(/[?/]/)[0];
  const {
    report,
    setReport,
    isLoading: reportLoading,
  } = useReport({ reportId });
  const { isLoading: dataLoading } = useContext(GroupedLoadingContext);
  const [ready, setReady] = useState(false);

  // Ready once the report has loaded AND nothing in the DOM is still loading
  // (spinners or gadget skeletons — see PRINT_LOADING_SELECTOR). Report gadgets
  // fetch their data independently of GroupedLoadingContext (which can read idle
  // while gadgets still load), so loading-placeholder absence is the reliable
  // "fully rendered" signal. Require it stable across a few polls, then
  // wait for fonts + two paints. A hard cap guarantees we always signal.
  useEffect(() => {
    if (ready || reportLoading || !report) return;
    let cancelled = false;
    let stable = 0;
    const finish = async () => {
      if (document.fonts?.ready) {
        await document.fonts.ready.catch(() => undefined);
      }
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      if (!cancelled) setReady(true);
    };
    const poll = setInterval(() => {
      const stillLoading =
        dataLoading ||
        document.querySelectorAll(PRINT_LOADING_SELECTOR).length > 0 ||
        // Wait for grid cards to finish refitting to their content (see
        // ReportCanvas buildHeadlessRefitLayout) before capturing.
        document.querySelectorAll('[data-headless-refit-pending="true"]')
          .length > 0;
      if (stillLoading) {
        stable = 0;
        return;
      }
      stable += 1;
      if (stable >= 3) {
        clearInterval(poll);
        finish();
      }
    }, POLL_MS);
    const cap = setTimeout(() => {
      clearInterval(poll);
      if (!cancelled) setReady(true);
    }, MAX_WAIT_MS);
    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimeout(cap);
    };
  }, [ready, reportLoading, report, dataLoading]);

  return (
    <PrintRoot
      id="print-report-root"
      data-print-ready={ready ? 'true' : undefined}
    >
      <PrintGlobalStyle />
      {report && (
        <WeekStartsOnOverrideContext.Provider
          value={{ weekStartsOnOverride: report.weekStartsOnOverride }}
        >
          <Report
            report={report}
            key={report.id}
            setReport={setReport}
            forceHeadlessRender
          />
        </WeekStartsOnOverrideContext.Provider>
      )}
    </PrintRoot>
  );
};

export default PrintReportShow;
